/*
 * Nouvel Heritage — URL-scoped accessibility stopgaps for content-author fixes.
 *
 * ─────────────────────────────────────────────────────────────────────
 * This file is a stopgap, not an architecture.
 * ─────────────────────────────────────────────────────────────────────
 *
 * Every handler below exists because specific content (a page, a blog
 * post, a rich-text block) was authored with incorrect or missing
 * accessibility markup in Shopify's admin. The correct fix for each one
 * is to re-author the content inside Shopify — set the right heading
 * level in the RTE, set the image alt text in the theme editor, etc.
 * JavaScript is the wrong layer to patch content-authoring mistakes,
 * because:
 *
 *   1. It couples runtime behavior to string-matched URLs, which breaks
 *      silently when slugs or locales change.
 *   2. It runs after paint, so assistive tech using a snapshot taken at
 *      load can miss the correction.
 *   3. It's invisible to merchandisers editing those pages, so the
 *      underlying mistake never gets corrected at the source.
 *
 * Each handler is annotated with a TODO describing the proper fix. When
 * a fix is applied in Shopify admin / Liquid, delete the matching
 * handler. When every handler is gone, delete this file AND the
 * <script src="a11y-content-fixes.js"> tag in layout/theme.liquid.
 *
 * ─────────────────────────────────────────────────────────────────────
 * History
 * ─────────────────────────────────────────────────────────────────────
 * This file replaces assets/a11y.js (deleted 2026-04-19), which had
 * grown into a document-wide accessibility patch layer over several
 * years. V20 through V29 progressively relocated every real handler
 * out into the component/template/locale file where it belonged
 * (store-locator, localization, cart remove announcer, image/cart
 * link aria-labels, hero video pause/play, RTE table polish, hr
 * decorative marking). What remained were these URL-scoped content
 * fixes, which have no proper component home — they live here until
 * the content itself is corrected.
 */

document.addEventListener('DOMContentLoaded', () => {
  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  function renameElementTag(oldElementsInput, newTagName) {
    const oldElements = Array.isArray(oldElementsInput) ? oldElementsInput : [oldElementsInput];
    const newElements = [];
    oldElements.forEach((oldElement) => {
      if (!oldElement || !oldElement.parentNode) return;
      const newElement = document.createElement(newTagName);
      Array.from(oldElement.attributes).forEach((attr) => {
        newElement.setAttribute(attr.name, attr.value);
      });
      while (oldElement.firstChild) {
        newElement.appendChild(oldElement.firstChild);
      }
      oldElement.parentNode.replaceChild(newElement, oldElement);
      newElements.push(newElement);
    });
    return newElements.length === 1 ? newElements[0] : newElements;
  }

  /* ------------------------------------------------------------------ *
   * 10 Years of Nouvel Heritage blog post — manual heading levels
   *
   * TODO (content author): Re-author the "10 Years of Nouvel Heritage"
   * blog post in both locales (/blogs/in-conversation/... and
   * /blogs/echanges-inspirants/...) so the visual "h1" is authored as
   * an <h1> in the RTE and the visual "h2" is authored as an <h2>.
   * Once the markup is correct, delete this handler.
   * WCAG 1.3.1 Info and Relationships, 2.4.6 Headings and Labels.
   * ------------------------------------------------------------------ */
  if (
    window.location.href.includes('blogs/in-conversation/10-years-of-nouvel-heritage') ||
    window.location.href.includes('blogs/echanges-inspirants/10-years-of-nouvel-heritage')
  ) {
    const h1Target = document.querySelector('rte-formatter h2');
    const h2Target = document.querySelector('rte-formatter h5');
    if (h1Target && h2Target) {
      h1Target.setAttribute('role', 'heading');
      h1Target.setAttribute('aria-level', '1');
      h2Target.setAttribute('role', 'heading');
      h2Target.setAttribute('aria-level', '2');
    }
  }

  /* ------------------------------------------------------------------ *
   * Warranty page — rte-formatter h4 elements promoted to heading level 2
   *
   * TODO (content author): Open /pages/warranty and /fr/pages/garantie
   * in Shopify admin. In each RTE block, change the visual-only <h4>
   * headings to real <h2> headings using the editor's heading dropdown.
   * Then delete this handler. WCAG 1.3.1, 2.4.6.
   * ------------------------------------------------------------------ */
  if (
    window.location.href.includes('pages/warranty') ||
    window.location.href.includes('fr/pages/garantie')
  ) {
    const block = document.querySelectorAll('.shopify-block rte-formatter')[0];
    if (block) {
      block.querySelectorAll('h4').forEach((heading) => {
        heading.setAttribute('role', 'heading');
        heading.setAttribute('aria-level', '2');
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Inside NH page — hero image alt
   *
   * TODO (content author): This is a latent i18n bug. The current
   * fallback is hardcoded English, so French visitors get an English
   * alt string. Open the Inside NH page in the Shopify theme editor
   * and set the hero image's alt text in BOTH locales (Translate &
   * Adapt). Once set, delete this handler. WCAG 1.1.1 Non-text Content.
   * ------------------------------------------------------------------ */
  if (window.location.href.includes('pages/inside-nh')) {
    const heroImg = document.querySelector('.hero__media-wrapper img');
    if (heroImg) {
      // F-18 (2026-05-12): the previous version hardcoded an English alt
      // string that surfaced on /fr/pages/inside-nh — French screen readers
      // would pronounce the English words with French phonetics. Branch on
      // `<html lang>` so each locale gets a native-language description.
      // The long-term fix remains setting alt via Translate & Adapt and
      // deleting this handler entirely.
      const isFr = (document.documentElement.lang || '').toLowerCase().startsWith('fr');
      heroImg.setAttribute(
        'alt',
        isFr
          ? "Gros plan sur les mains d'un artisan qui polit un bracelet en or sur un touret de polissage."
          : "A close-up view shows a craftsman's hands polishing a gleaming gold bracelet on a spinning buffing wheel."
      );
    }
  }

  /* ------------------------------------------------------------------ *
   * Privacy policy page
   *   - Remove broken dinner-date-mood link (was href-only, no visible UI)
   *   - Promote rich-text h3 to heading level 2
   *
   * TODO (content author): Edit /pages/privacy-policy (and the French
   * version) in Shopify admin. Remove the orphan link pointing to
   * /collections/dinner-date-mood — it has no visible text or image,
   * so it exists only as a screen-reader trap. Also change the visual
   * <h3> to an <h2> via the RTE heading dropdown. Then delete this
   * handler. WCAG 1.3.1, 2.4.4 Link Purpose.
   * ------------------------------------------------------------------ */
  /* Audit N-08 (2026-07): the canonical page lives at
     /policies/privacy-policy — the previous match only caught the
     /pages/ alias, so the fix never ran on the canonical URL. */
  if (/(pages|policies)\/privacy-policy/.test(window.location.href)) {
    document
      .querySelectorAll('.group-block-content a[href$="/collections/dinner-date-mood"].image-block')
      .forEach((link) => {
        link.removeAttribute('href');
        renameElementTag(link, 'div');
      });

    document.querySelectorAll('.shopify-section .c-richtext.t-text h3').forEach((h) => {
      h.setAttribute('role', 'heading');
      h.setAttribute('aria-level', '2');
    });
  }

  /* ------------------------------------------------------------------ *
   * Bangles size guide — add <abbr> around "cm" in table headers
   *
   * TODO (content author): Open /pages/bangles-size-guide in Shopify
   * admin and edit the table in the RTE directly. In the source view,
   * replace "(cm)" with "(<abbr title=\"centimetres\">cm</abbr>)" in
   * each header cell. (The Shopify RTE supports raw HTML in source
   * mode.) Then delete this handler. WCAG 3.1.4 Abbreviations.
   * ------------------------------------------------------------------ */
  if (window.location.href.includes('pages/bangles-size-guide')) {
    document.querySelectorAll('.rte-table-wrapper table thead p').forEach((p) => {
      if (p.querySelector('abbr')) return;
      if (p.innerHTML.includes('(cm)')) {
        p.innerHTML = p.innerHTML.replace('(cm)', '(<abbr title="centimetres">cm</abbr>)');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Ring size guide — promote rte-formatter <p> to heading level 2
   *
   * TODO (content author): Open /pages/ring-size-guide in Shopify
   * admin. The visually-styled section title is authored as a <p>;
   * change it to an <h2> using the RTE heading dropdown. Then delete
   * this handler. WCAG 1.3.1, 2.4.6.
   * ------------------------------------------------------------------ */
  if (window.location.href.includes('pages/ring-size-guide')) {
    const p = document.querySelector('.shopify-block rte-formatter p');
    if (p) {
      p.setAttribute('aria-level', '2');
      p.setAttribute('role', 'heading');
    }
  }

  /* ------------------------------------------------------------------ *
   * FAQs page — descriptive aria-labels for external links
   *
   * TODO (content author): Open /pages/faqs (and /fr/pages/faqs) in
   * Shopify admin and re-author each of the four external-destination
   * links so the VISIBLE link text itself describes the destination
   * (e.g., "Visit our Returns Center" instead of "here"). Once visible
   * text is descriptive, the aria-label workaround is unnecessary.
   * Delete this handler when done. WCAG 2.4.4 Link Purpose (In Context),
   * 2.4.9 Link Purpose (Link Only).
   *
   * If rewriting the visible copy is not possible, move these EN and
   * FR strings into the page's Liquid template or the locale files
   * rather than leaving them hardcoded here.
   * ------------------------------------------------------------------ */
  if (window.location.href.includes('pages/faqs')) {
    /**
     * C-09 (2026-05-12): single source of truth for the FAQ a11y fixes.
     * Each entry matches by `pathname.endsWith()` (or hostname for the
     * external Returns Center) and provides translated aria-labels. The
     * previous code matched against six concrete URL shapes per link
     * (root domain × locale × naming variant), which broke silently
     * whenever marketing renamed a page. Centralizing here means a
     * page rename is a one-line update and a future locale is a new
     * key in the `aria` map. Long-term: move aria-labels onto the
     * pages themselves so this handler can be deleted.
     *
     * Brand spelling also fixed: the FR aria-labels read "Nouvel Héritage"
     * with the accent.
     */
    const isFr = (document.documentElement.lang || '').toLowerCase().startsWith('fr');

    /** @type {{ matchHostname?: string, matchPathSuffix?: string[], aria: { en: string, fr: string } }[]} */
    const A11Y_LINK_FIXES = [
      {
        // External Returns Center is a separate hostname, no locale prefix.
        matchHostname: 'nouvelheritage.returnscenter.com',
        aria: {
          en: 'Click here to return to Nouvel Heritage Returns Center page (opens in a new tab).',
          fr: 'Cliquez ici pour revenir à la page du Centre de retours Nouvel Héritage (ouvre dans un nouvel onglet).',
        },
      },
      {
        // Bangle / wrist size guide. Matches both legacy "bangles" slug
        // and the future "wrist" slug per the wrist-not-bangle rule.
        matchPathSuffix: ['/pages/bangles-size-guide', '/pages/wrist-size-guide'],
        aria: {
          en: 'Click here to review our wrist size guide page (opens in a new tab).',
          fr: 'Cliquez ici pour consulter notre guide des tailles de poignet (ouvre dans un nouvel onglet).',
        },
      },
      {
        matchPathSuffix: ['/pages/ring-size-guide'],
        aria: {
          en: 'Click here to review our ring size guide page (opens in a new tab).',
          fr: 'Cliquez ici pour consulter notre guide des tailles de bagues (ouvre dans un nouvel onglet).',
        },
      },
      {
        // Refund policy lives at /policies/ on the canonical store and
        // /pages/ on the legacy US mirror — match both via path suffix.
        matchPathSuffix: ['/policies/refund-policy', '/pages/refund-policy'],
        aria: {
          en: 'Read more about our refund policy (opens in a new tab).',
          fr: 'Pour en savoir plus sur notre politique de remboursement, cliquez ici (ouvre dans un nouvel onglet).',
        },
      },
    ];

    document.querySelectorAll('a[href]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      let linkUrl;
      try {
        linkUrl = new URL(link.href, window.location.origin);
      } catch (e) {
        return;
      }
      const match = A11Y_LINK_FIXES.find((entry) => {
        if (entry.matchHostname && linkUrl.hostname === entry.matchHostname) return true;
        if (entry.matchPathSuffix) {
          return entry.matchPathSuffix.some((suffix) => linkUrl.pathname.endsWith(suffix));
        }
        return false;
      });
      if (match) {
        link.setAttribute('aria-label', isFr ? match.aria.fr : match.aria.en);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Find Your Mood — presentation role on decorative images
   *
   * TODO (content author): Open /pages/find-your-mood in the Shopify
   * theme editor and mark the two purely-decorative image blocks as
   * decorative (empty alt or "Decorative image" toggle). That sets
   * role="presentation" / alt="" natively with no JS. Delete this
   * handler when done. WCAG 1.1.1 Non-text Content.
   * ------------------------------------------------------------------ */
  if (window.location.href.includes('/pages/find-your-mood')) {
    const imgs = document.querySelectorAll('.image-block.size-style img');
    if (imgs[0]) imgs[0].setAttribute('role', 'presentation');
    if (imgs[1]) imgs[1].setAttribute('role', 'presentation');
  }

  /* ------------------------------------------------------------------ *
   * Customer profile — strip obfuscated third-party aria-label
   *
   * TODO (investigation needed): The `._1fragem32._1fragemn2` class is
   * an obfuscated build artifact, likely from Shopify's Customer
   * Accounts UI extension. Figure out which extension/app is injecting
   * the bad aria-label and either (a) configure it correctly in its
   * source repo, (b) override it via extension settings, or (c) raise
   * it with Shopify support if it's a platform-level bug. Once the
   * upstream source is clean, delete this handler.
   * ------------------------------------------------------------------ */
  if (window.location.href.includes('/profile')) {
    const wrongName = document.querySelector('#profile-details ._1fragem32._1fragemn2');
    if (wrongName) wrongName.removeAttribute('aria-label');
  }
});

/* ------------------------------------------------------------------ *
 * Front Chat widget (third party) — audit N-17, WCAG 4.1.2.
 * Raised with Front — delete when their embed ships a fix.
 *
 * V3 (2026-08-20): V1/V2 never worked because they queried the TOP
 * document. Live DOM inspection showed #front-chat-holder lives INSIDE
 * #front-chat-iframe, a same-origin srcless iframe (axe reported the
 * frame-prefixed target "#front-chat-iframe, #front-chat-holder" — the
 * comma is axe's frame path separator, not two selectors). Inside that
 * document:
 *   - #front-chat-holder is div[role="button"][tabindex="0"] with no
 *     accessible name (aria-command-name), wrapping a second
 *     div[role="button"][tabindex="0"][aria-label="Show"]
 *     (nested-interactive).
 *   - further role="button" divs/svgs are decorative.
 * The patch works on iframe.contentDocument: strip role/tabindex from
 * the holder, keep the inner launcher as the single control and give
 * it a concierge label ("Show" is meaningless out of context), and
 * strip role from unnamed non-focusable role="button" elements.
 * Verified live on staging 2026-08-20: axe clean inside the frame,
 * launcher still opens/closes, and Front did NOT re-assert attributes
 * after a full open/close re-render cycle. Observers on both documents
 * remain as insurance: outer childList catches (re)mounting of the
 * iframe, inner childList+attribute mutations catch any vendor
 * re-assertion. Removal is conditional, so our own mutations cannot
 * loop.
 * ------------------------------------------------------------------ */
(function () {
  var LABEL = (document.documentElement.lang || '').toLowerCase().startsWith('fr')
    ? 'Discuter avec votre concierge'
    : 'Chat with your concierge';
  var innerObserver = null;
  var observedDoc = null;

  function getFrontDoc() {
    var iframe = document.getElementById('front-chat-iframe');
    if (!iframe) return null;
    try {
      return iframe.contentDocument;
    } catch (e) {
      return null; // cross-origin: nothing we can do
    }
  }

  function accessibleName(el) {
    if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')) return true;
    if (el.tagName.toLowerCase() === 'svg') {
      var t = el.querySelector('title');
      return Boolean(t && t.textContent && t.textContent.trim());
    }
    return Boolean(el.textContent && el.textContent.trim());
  }

  function patchFrontChat() {
    var iframe = document.getElementById('front-chat-iframe');
    if (iframe && !iframe.getAttribute('title')) {
      iframe.setAttribute('title', LABEL);
    }

    var doc = getFrontDoc();
    if (!doc || !doc.body) return;

    // (Re)attach the inner observer whenever the iframe document changes.
    if (observedDoc !== doc) {
      if (innerObserver) innerObserver.disconnect();
      observedDoc = doc;
      innerObserver = new MutationObserver(patchFrontChat);
      innerObserver.observe(doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['role', 'tabindex', 'aria-label'],
      });
    }

    // Only the vendor default launcher needs repair. When it is not rendered,
    // the frame holds the chat panel, whose controls are left untouched.
    var holder = doc.getElementById('front-chat-holder');
    if (!holder) return;
    if (holder.hasAttribute('role')) holder.removeAttribute('role');
    if (holder.hasAttribute('tabindex')) holder.removeAttribute('tabindex');

    var buttons = holder.querySelectorAll('[role="button"]');
    for (var i = 0; i < buttons.length; i++) {
      var el = buttons[i];
      if (el.getAttribute('tabindex') === '0') {
        var label = el.getAttribute('aria-label');
        if (!label || label === 'Show') el.setAttribute('aria-label', LABEL);
      } else if (!accessibleName(el)) {
        el.removeAttribute('role');
      }
    }
  }

  var watchedIframe = null;

  var outerObserver = new MutationObserver(function () {
    var iframe = document.getElementById('front-chat-iframe');
    if (!iframe) return;
    if (iframe === watchedIframe && getFrontDoc() === observedDoc) return;
    if (iframe !== watchedIframe) {
      watchedIframe = iframe;
      iframe.addEventListener('load', patchFrontChat);
    }
    patchFrontChat();
  });
  outerObserver.observe(document.body, { childList: true, subtree: true });
  patchFrontChat();
})();
